// src/pages/Admin/SupportRequests.js

import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  useMediaQuery,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Snackbar,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { database } from "../../firebase"; // Adjust the path as necessary
import {
  ref,
  onValue,
  off,
  remove,
} from "firebase/database";
import { useTheme } from "@mui/material/styles";
import TopBar from "../Sidebar/SidebarAdmin"; // Adjust the import path as necessary

const SupportRequests = () => {
  // State variables
  const [supportRequests, setSupportRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for Filters
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // State for Dialogs
  const [openViewDetails, setOpenViewDetails] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);

  // State for Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch support requests from Firebase
  useEffect(() => {
    const supportRequestsRef = ref(database, "supportRequests");

    const handleData = (snapshot) => {
      const data = snapshot.val() || {};
      const requests = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
      }));
      setSupportRequests(requests);
      extractFilters(requests);
      setLoading(false);
    };

    const handleError = (error) => {
      console.error("Error fetching support requests:", error);
      setError("Failed to load support requests.");
      setLoading(false);
    };

    onValue(supportRequestsRef, handleData, handleError);

    // Cleanup listener on unmount
    return () => {
      off(supportRequestsRef, "value", handleData);
    };
  }, []);

  // Extract unique types and subjects for filters
  const extractFilters = (requests) => {
    const uniqueTypes = [...new Set(requests.map((req) => req.type))];
    const uniqueSubjects = [...new Set(requests.map((req) => req.subject))];
    setTypes(uniqueTypes);
    setSubjects(uniqueSubjects);
  };

  // Handle filter changes
  const handleTypeChange = (event) => {
    setSelectedType(event.target.value);
  };

  const handleSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Filter support requests based on selected type, subject, and search query
  const filteredRequests = supportRequests.filter((req) => {
    const matchesType = selectedType === "" || req.type === selectedType;
    const matchesSubject = selectedSubject === "" || req.subject === selectedSubject;
    const matchesSearch =
      searchQuery === "" ||
      req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      req.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSubject && matchesSearch;
  });

  // Handle viewing details
  const handleViewDetails = (request) => {
    setCurrentRequest(request);
    setOpenViewDetails(true);
  };

  const handleCloseViewDetails = () => {
    setOpenViewDetails(false);
    setCurrentRequest(null);
  };

  // Handle deleting a support request
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this support request?")) {
      const requestRef = ref(database, `supportRequests/${id}`);
      remove(requestRef)
        .then(() => {
          setSnackbar({
            open: true,
            message: "Support request deleted successfully.",
            severity: "success",
          });
        })
        .catch((err) => {
          console.error("Error deleting support request:", err);
          setSnackbar({
            open: true,
            message: "Failed to delete support request.",
            severity: "error",
          });
        });
    }
  };

  // Handle Snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Helper function to format timestamps
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Loading State
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <TopBar />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: isMobile ? 1 : 3,
          paddingTop: 10,
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
          overflow: "hidden", // Prevent vertical scroll
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
          Support Requests Management
        </Typography>

        {/* Filters and Search */}
        <Paper
          elevation={3}
          sx={{
            padding: isMobile ? 1 : 2,
            marginBottom: isMobile ? 2 : 4,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={isMobile ? 1 : 2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="type-filter-label">Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={selectedType}
                  label="Type"
                  onChange={handleTypeChange}
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {types.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="subject-filter-label">Subject</InputLabel>
                <Select
                  labelId="subject-filter-label"
                  value={selectedSubject}
                  label="Subject"
                  onChange={handleSubjectChange}
                  size={isMobile ? "small" : "medium"}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {subjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  endAdornment: <SearchIcon />,
                  style: { paddingRight: isMobile ? 8 : 12 },
                }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Support Requests Display */}
        {isMobile ? (
          // Mobile View: Card Layout
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              overflowY: "auto",
              maxHeight: "calc(100vh - 200px)", // Adjust based on header/footer height
            }}
          >
            {filteredRequests.length > 0 ? (
              filteredRequests.map((req) => (
                <Card key={req.id} variant="outlined" sx={{ padding: 1 }}>
                  <CardContent sx={{ paddingBottom: "8px !important" }}>
                    <Typography variant="subtitle1" gutterBottom>
                      <strong>{req.name}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email:</strong> {req.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Subject:</strong> {req.subject}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Type:</strong> {req.type.charAt(0).toUpperCase() + req.type.slice(1)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created At:</strong> {formatDate(req.createdAt)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ padding: 0 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(req)}
                        size={isMobile ? "small" : "medium"}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Request">
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(req.id)}
                        size={isMobile ? "small" : "medium"}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              ))
            ) : (
              <Typography variant="body1" align="center">
                No support requests found.
              </Typography>
            )}
          </Box>
        ) : (
          // Desktop View: Table Layout
          <Paper
            elevation={3}
            sx={{ padding: 2, borderRadius: 2, overflowY: "auto", maxHeight: "70vh" }}
          >
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Type</TableCell>
                    {!isMobile && <TableCell>Message</TableCell>}
                    <TableCell>Created At</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.length > 0 ? (
                    filteredRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>{req.name}</TableCell>
                        <TableCell>{req.email}</TableCell>
                        <TableCell>{req.subject}</TableCell>
                        <TableCell>
                          {req.type.charAt(0).toUpperCase() + req.type.slice(1)}
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {req.message.length > 50
                              ? `${req.message.substring(0, 50)}...`
                              : req.message}
                          </TableCell>
                        )}
                        <TableCell>{formatDate(req.createdAt)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              onClick={() => handleViewDetails(req)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Request">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(req.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No support requests found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* View Details Dialog */}
        <Dialog
          open={openViewDetails}
          onClose={handleCloseViewDetails}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Support Request Details</DialogTitle>
          <DialogContent dividers>
            {currentRequest && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      <strong>Name:</strong>
                    </Typography>
                    <Typography variant="body2">{currentRequest.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      <strong>Email:</strong>
                    </Typography>
                    <Typography variant="body2">{currentRequest.email}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      <strong>Subject:</strong>
                    </Typography>
                    <Typography variant="body2">{currentRequest.subject}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      <strong>Type:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {currentRequest.type.charAt(0).toUpperCase() +
                        currentRequest.type.slice(1)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      <strong>Message:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {currentRequest.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      <strong>Created At:</strong>
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(currentRequest.createdAt)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseViewDetails} variant="contained" size={isMobile ? "small" : "medium"}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default SupportRequests;
